import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import '../../../components/ui/badge';
import { Card, CardContent } from '../../../components/ui/card';
import { AlertTriangle, Package, Hash, Trash2, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

import { useProducts } from '../Utils/useProducts';
import { formatPrice } from '../Utils/productsSchema';

export const DeleteProductModal = ({
  isOpen,
  onClose,
  onConfirm,
  onLoading,
  product,
  clientId
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const { deleteProduct } = useProducts(clientId);

  const handleDelete = async () => {
    if (!product || !clientId) return;

    try {
      setIsDeleting(true);
      onLoading?.(true);

      const additionalData = {
        reason: deleteReason || 'Eliminado desde la interfaz de productos',
        deleted_by: 'user', // Could be enhanced to include actual user info
        timestamp: new Date().toISOString()
      };

      const response = await deleteProduct(product.id, additionalData);

      if (response.success) {
        onConfirm?.();
        onClose();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setIsDeleting(false);
      onLoading?.(false);
    }
  };

  if (!isOpen || !product) return null;

  const getStatusInfo = () => {
    if (product.active) {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        label: 'Activo'
      };
    } else {
      return {
        icon: AlertCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        label: 'Inactivo'
      };
    }
  };

  const statusInfo = getStatusInfo();
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span>Eliminar Producto</span>
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. El producto será eliminado
            permanentemente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Info Card */}
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-4">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 rounded bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white flex-shrink-0">
                  <Package className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {product.name || "Sin nombre"}
                  </h3>
                  {product.sku && (
                    <div className="flex items-center space-x-1 text-sm text-slate-600 mt-1">
                      <Hash className="h-3 w-3" />
                      <span className="font-mono">{product.sku}</span>
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                    {product.price && (
                      <div>
                        <span className="font-medium">Precio: </span>
                        {formatPrice(product.price)}
                      </div>
                    )}
                    {product.description && (
                      <div className="col-span-2">
                        <span className="font-medium">Descripción: </span>
                        {product.description}
                      </div>
                    )}
                    {product.items && product.items.length > 0 && (
                      <div>
                        <span className="font-medium">Items: </span>
                        {product.items.length}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warning Message */}
          <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <div className="font-semibold mb-1">Advertencia</div>
              <ul className="space-y-1 text-xs">
                <li>• Este producto será eliminado permanentemente</li>
                <li>• Se perderán todas las asociaciones con items</li>
                <li>
                  • Los históricos y referencias en otras secciones pueden verse
                  afectados
                </li>
                {product.active && (
                  <li className="font-medium">
                    • El producto está actualmente ACTIVO
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Optional Delete Reason */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Motivo de eliminación (opcional)
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Especifica el motivo por el cual eliminas este producto..."
              className="w-full p-3 text-sm border border-slate-200 rounded focus:border-red-400 focus:ring-red-400/20 resize-none"
              rows={3}
              disabled={isDeleting}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar Producto
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 